import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToken } from "@/lib/session";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Type, ArrowLeft, Trophy, Users, Copy, LogIn } from "lucide-react";
import { applyTheme } from "@/lib/themes";

export default function WordBuildingPage() {
  const token = useToken();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [mode, setMode] = useState("solo"); // solo | room
  const [data, setData] = useState(null);
  const [room, setRoom] = useState(null);
  const [word, setWord] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);
  const [period, setPeriod] = useState("weekly");
  const [joinCode, setJoinCode] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [meR, dR] = await Promise.all([
        api.get(`/employee/me?token=${token}`),
        api.get(`/word/today?token=${token}`),
      ]);
      setMe(meR.data); applyTheme(meR.data.theme || "standard");
      setData(dR.data);
    } catch (e) { console.error("word load failed", e); toast.error("Load failed"); }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    api.get(`/word/leaderboard?token=${token}&period=${period}`).then(r=>setLeaderboard(r.data)).catch(()=>{});
  }, [period, token, data]);

  // Real-time room state via WebSocket (replaces 3s polling).
  useEffect(() => {
    if (!room?.code) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = base.replace(/^http/, "ws");
    const url = `${wsBase}/api/word/ws/room/${room.code}?token=${encodeURIComponent(token)}`;
    let ws;
    let retryTimer;
    let cancelled = false;
    const connect = () => {
      if (cancelled) return;
      ws = new WebSocket(url);
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "room_update" || msg.type === "player_joined") {
            if (msg.state) setRoom((cur) => ({ ...(cur || {}), ...msg.state }));
          }
        } catch { /* ignore malformed frame */ }
      };
      ws.onclose = () => {
        if (!cancelled) retryTimer = setTimeout(connect, 2000);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* already closed */ } };
    };
    connect();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      try { ws && ws.close(); } catch { /* already closed */ }
    };
    // eslint-disable-next-line
  }, [room?.code, token]);

  const submitWord = async () => {
    if (!word.trim()) return;
    try {
      const r = await api.post(`/word/submit?token=${token}`, { word, room_code: room?.code || null });
      if (mode === "room") {
        if (r.data.accepted) toast.success(`+${r.data.score} for ${r.data.word}`);
        else toast.error(r.data.reason || "Not accepted");
        // WebSocket broadcasts the updated room state automatically.
      } else {
        // Solo daily = guess-the-word. No correct/wrong feedback here.
        if (r.data.accepted) toast.success("Guess locked in! The answer is revealed in the daily results.");
        else toast.message(r.data.reason || "Already submitted");
        refresh();
      }
      setWord("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
  };

  const createRoom = async () => {
    try {
      const r = await api.post(`/word/room/create?token=${token}`, { max_players: 6 });
      setRoom(r.data); setMode("room"); toast.success(`Room ${r.data.code} created`);
    } catch { toast.error("Create failed"); }
  };

  const joinRoom = async () => {
    if (!joinCode.trim()) return;
    try {
      const r = await api.post(`/word/room/${joinCode.toUpperCase()}/join?token=${token}`);
      setRoom(r.data); setMode("room"); toast.success(`Joined ${r.data.code}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Join failed"); }
  };

  const copyInvite = () => {
    if (!room) return;
    const url = `${window.location.origin}/word?token=${token}&room=${room.code}`;
    navigator.clipboard.writeText(url); toast.success("Invite link copied");
  };

  const letters = (mode === "room" ? room?.letters : data?.letters) || "";

  return (
    <div className="min-h-screen themed-bg">
      <header className="border-b-4 px-6 py-4 flex justify-between items-center themed-border" style={{background: "var(--bq-header-bg)"}}>
        <div className="flex items-center gap-3">
          <button onClick={()=>navigate(-1)} data-testid="back-to-hub" className="p-2 border-2 themed-border" style={{background: "var(--bq-card-bg)"}}><ArrowLeft size={18}/></button>
          <div className="w-10 h-10 flex items-center justify-center" style={{background: "var(--bq-text)"}}><Type size={22} style={{color: "var(--bq-accent)"}}/></div>
          <div><div className="font-black uppercase">Word Building</div><div className="text-xs font-mono-bq" style={{color: "var(--bq-muted)"}}>1–6 players · 3+ letter words</div></div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="mode-solo" onClick={()=>{setMode("solo"); setRoom(null);}} className="px-3 py-1 border-2 themed-border font-black text-xs uppercase" style={{background: mode==="solo" ? "var(--bq-text)" : "var(--bq-card-bg)", color: mode==="solo" ? "var(--bq-bg)" : "var(--bq-text)"}}>Solo</button>
          <button data-testid="mode-room" onClick={()=>setMode("room")} className="px-3 py-1 border-2 themed-border font-black text-xs uppercase" style={{background: mode==="room" ? "var(--bq-text)" : "var(--bq-card-bg)", color: mode==="room" ? "var(--bq-bg)" : "var(--bq-text)"}}>Multiplayer</button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {mode === "room" && !room && (
            <div className="brutal-card p-6">
              <h3 className="font-black uppercase text-lg mb-3 flex items-center gap-2"><Users size={18}/> Play with the team</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-black uppercase text-sm mb-2">Host a room</h4>
                  <button data-testid="create-room-btn" onClick={createRoom} className="brutal-btn w-full">Create Room</button>
                </div>
                <div>
                  <h4 className="font-black uppercase text-sm mb-2">Join a room</h4>
                  <div className="flex gap-2">
                    <input data-testid="room-code-input" value={joinCode} onChange={e=>setJoinCode(e.target.value.toUpperCase())} placeholder="ROOM CODE" className="brutal-input"/>
                    <button data-testid="join-room-btn" onClick={joinRoom} className="brutal-btn"><LogIn size={16}/></button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="brutal-card p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-black uppercase text-lg">{mode === "room" ? "Today's Letters" : "Unscramble Today's Word"}</h3>
              {mode === "room" && room && (
                <div className="flex items-center gap-2">
                  <span className="font-mono-bq font-black text-lg px-3 py-1 border-2 themed-border" style={{background: "var(--bq-accent2)"}}>{room.code}</span>
                  <button onClick={copyInvite} title="Copy invite" className="p-1 border-2 themed-border"><Copy size={14}/></button>
                </div>
              )}
            </div>
            {mode !== "room" && (
              <p className="text-sm mb-2" style={{color: "var(--bq-muted)"}}>Rearrange these letters to find today's word. You get <strong>one guess</strong> — the correct word and its meaning are revealed in the daily results after the round closes.</p>
            )}
            <div className="flex justify-center gap-2 my-4 flex-wrap">
              {letters.split("").map((L, i) => (
                <div key={`${L}-${i}`} className="w-14 h-14 flex items-center justify-center font-black text-3xl border-4 themed-border" style={{background: "var(--bq-card-bg)", boxShadow: "4px 4px 0 var(--bq-text)"}}>{L}</div>
              ))}
            </div>
            {mode !== "room" && data?.guessed ? (
              <div className="border-4 themed-border p-4 text-center" data-testid="word-guess-locked" style={{background: "var(--bq-accent2)"}}>
                <div className="text-xs font-black uppercase" style={{color: "var(--bq-muted)"}}>Your guess is locked in</div>
                <div className="text-2xl font-black uppercase tracking-widest my-1" data-testid="word-guess-value">{data.guess}</div>
                <div className="text-xs" style={{color: "var(--bq-muted)"}}>The correct word &amp; its meaning will be revealed in the daily results after today's round ends. Good luck!</div>
              </div>
            ) : (
              <div className="flex gap-2 mt-4">
                <input data-testid="word-input" value={word} onChange={(e)=>setWord(e.target.value)} placeholder={mode === "room" ? "TYPE A WORD..." : "TYPE YOUR GUESS..."} className="brutal-input uppercase tracking-widest" onKeyDown={(e)=>e.key==="Enter" && submitWord()}/>
                <button data-testid="word-submit" onClick={submitWord} className="brutal-btn">{mode === "room" ? "Add" : "Guess"}</button>
              </div>
            )}
          </div>

          {mode === "room" && room && (
            <div className="brutal-card p-5">
              <h3 className="font-black uppercase mb-3 flex items-center gap-2"><Users size={18}/> Players ({room.players.length}/{room.max_players})</h3>
              <div className="space-y-2">
                {room.players.map((p, i) => (
                  <div key={p.user_id} className="border-2 themed-border p-3" style={{background: p.is_self ? "var(--bq-accent2)" : "var(--bq-card-bg)"}}>
                    <div className="flex justify-between items-center mb-1">
                      <div className="font-black flex items-center gap-2"><span className="w-6 h-6 flex items-center justify-center text-xs" style={{background:"var(--bq-text)", color:"var(--bq-bg)"}}>{i+1}</span>{p.name}{p.is_self ? " (you)" : ""}</div>
                      <div className="font-mono-bq font-black">{p.score}</div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {p.words.map((w, j) => (
                        <span key={`${w.word}-${j}`} className="text-xs px-2 py-0.5 border" style={{borderColor: "var(--bq-text)"}}>{w.word}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside>
          <div className="brutal-card p-5 sticky top-4">
            <h3 className="text-xl font-black uppercase flex items-center gap-2 mb-3"><Trophy size={20}/> Word Board</h3>
            <div className="flex gap-1 mb-3 border-2 themed-border p-1">
              {["daily","weekly","monthly","all"].map((p) => (
                <button key={p} onClick={()=>setPeriod(p)} className="flex-1 text-xs font-black uppercase py-1" style={{background: period===p ? "var(--bq-text)" : "var(--bq-card-bg)", color: period===p ? "var(--bq-bg)" : "var(--bq-text)"}}>{p}</button>
              ))}
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {leaderboard.length === 0 && <div className="text-sm text-center py-4" style={{color: "var(--bq-muted)"}}>No data yet.</div>}
              {leaderboard.map((r, i) => (
                <div key={r.employee_id} className="flex items-center justify-between p-2 border-2 themed-border" style={{background: r.employee_id === me?.employee_id ? "var(--bq-accent2)" : "var(--bq-card-bg)"}}>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center font-black text-xs" style={{background: i < 3 ? "var(--bq-accent2)" : "var(--bq-text)", color: i < 3 ? "#000" : "var(--bq-bg)"}}>{i+1}</div>
                    <div className="font-bold text-sm">{r.name}</div>
                  </div>
                  <div className="text-right"><div className="font-mono-bq font-black">{r.score}</div><div className="text-xs" style={{color: "var(--bq-muted)"}}>{r.words} words</div></div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}
